import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../CSS/Global.css';
import './CustomerStyle.css';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Mail, Phone, Search, Trash2, X } from 'lucide-react';
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { decryptToken } from '../utils/getToken';
import { toast } from 'react-toastify';
import {
  messageFromCustomerDeleteError,
  isAdmissionBlockedDeleteResponse,
} from './customerDeleteMessages';

const Customer = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const url = API_BASE_URL
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
  const [load, setLoad] = useState(false)
  const [selectedIds, setSelectedIds] = useState([]);
  const token = decryptToken("accessToken") || ""

  const fetchCustomers = async () => {
    try {
      setLoad(true); // Start loading
      const response = await fetch(`${url}/api/customer/all/${isLoggedIn.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const data = await response.json();
      const parsedCustomers = data.map((customer) => ({
        ...customer,
        billingAddress: customer.billingAddress ? JSON.parse(customer.billingAddress) : {},
        shippingAddress: customer.shippingAddress ? JSON.parse(customer.shippingAddress) : {},
        contact: customer.contact ? JSON.parse(customer.contact) : [],
      }));
      setCustomers(parsedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoad(false); // Stop loading after everything is done
    }
  };


  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, globalSearch, customDate.from, customDate.to, sortOrder, entriesPerPage]);

  // 1. Filter by date
  let processedCustomers = [...customers];

  if (customDate.from || customDate.to) {
    processedCustomers = processedCustomers.filter((customer) => {
      const createdAt = new Date(customer.createdAt);
      const from = customDate.from ? new Date(customDate.from) : null;
      const to = customDate.to ? new Date(customDate.to) : null;

      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  // 2. Sort by createdAt
  processedCustomers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const globalQ = String(globalSearch ?? "").trim();
  const searchRaw = `${searchQuery.trim()} ${globalQ}`.trim().toLowerCase();

  const filteredCustomers = processedCustomers.filter((customer) => {
    if (!searchRaw) return true;
    const hay = [
      customer.customerName,
      customer.companyName,
      customer.displayName,
      customer.customer_id,
      customer.cust_ID,
      customer.email,
      customer.phone1,
      customer.phone2,
      customer.gstNumber,
      customer.remarks,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(searchRaw);
  });

  const indexOfLastCustomer = currentPage * entriesPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / entriesPerPage) || 1);

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const totalLoaded = Array.isArray(customers) ? customers.length : 0;
  const totalInView = processedCustomers.length;
  const totalMatching = filteredCustomers.length;
  const searchActive = Boolean(searchQuery.trim() || globalQ);
  const pageLabelFrom = totalMatching === 0 ? 0 : indexOfFirstCustomer + 1;
  const pageLabelTo = Math.min(indexOfLastCustomer, totalMatching);

  const pageIds = currentCustomers.map((c) => c.id).filter(Boolean);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);
  const toggleImport = () => setImportOpen(!isImportOpen);
  const handleRefresh = () => {
    setLoading(true);
    fetchCustomers();
    setTimeout(() => setLoading(false), 1000);
  };

  const handleDelete = async (customerId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This customer will be deleted. Linked admission(s) will also be removed when allowed. Invoices and receipts will be kept.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${url}/api/customer/${customerId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            if (isAdmissionBlockedDeleteResponse(response.status, data)) {
              toast.error(messageFromCustomerDeleteError(data), {
                position: 'top-center',
                autoClose: 6000,
              });
              return;
            }
            throw new Error(data.error || data.message || 'Failed to delete');
          }

          const admissionNote =
            data.deletedAdmissionCount > 0
              ? ` (${data.deletedAdmissionCount} linked admission(s) removed)`
              : '';
          toast.success(
            (data.message || 'Customer deleted.') + admissionNote,
            { position: 'top-center' }
          );
          setCustomers(customers.filter((c) => c.id !== customerId));
        } catch (error) {
          console.error('Delete error:', error.message);
          toast.error(error.message || 'Failed to delete customer.', { position: 'top-center' });
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;

    Swal.fire({
      title: 'Delete selected customers?',
      html: `This will permanently delete <strong>${selectedIds.length}</strong> customer(s) and linked admission(s) when allowed. Invoices and receipts will be kept.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const response = await fetch(`${url}/api/customer/bulk-delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ids: selectedIds }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (isAdmissionBlockedDeleteResponse(response.status, data)) {
            toast.error(messageFromCustomerDeleteError(data), {
              position: 'top-center',
              autoClose: 6000,
            });
            return;
          }
          throw new Error(data.message || data.error || 'Bulk delete failed');
        }

        if (data.deletedIds?.length > 0) {
          toast.success(
            data.message ||
              `${data.deletedIds.length} customer(s) deleted.`,
            { position: 'top-center' }
          );
          setCustomers((prev) =>
            prev.filter((c) => !data.deletedIds.includes(c.id))
          );
        }
        if (data.blocked?.length > 0) {
          const names = data.blocked
            .map((b) => b.customerName || `#${b.customerId}`)
            .slice(0, 3)
            .join(', ');
          toast.error(
            `Skipped ${data.blocked.length} customer(s) (${names}${data.blocked.length > 3 ? '…' : ''}): Active or Paid admission.`,
            { position: 'top-center', autoClose: 6000 }
          );
        }
        setSelectedIds([]);
        await fetchCustomers();
      } catch (error) {
        console.error('Bulk delete error:', error);
        toast.error(error.message || 'Bulk delete failed.', { position: 'top-center' });
      }
    });
  };


  const handleEdit = (customer) => {
    navigate('/edit-customer', { state: customer });

  };

  const SkeletonLoader = () => (
    <div>
      {Array(10).fill('').map((_, index) => (
        <div className="skeleton-row" key={index}>
          {[...Array(5)].map((_, i) => <div className="skeleton" key={i}></div>)}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Loader />
    );
  }
  if (load) {
    return (
      <Loader />
    );
  }

  return (
    <>
      <div className="customers-header">
        <div className="customer-dropdown">
          <p className="dashboard-title" onClick={toggleDropdown}>
            All Customers
          </p>
        </div>

        <div className="customer-warning-new-hamburger-help">
          <div className="filter-controls">
            <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
              <i className="fa-solid fa-sliders"></i> Filters
            </button>
          </div>
          <div className="import-hamburger" onClick={toggleImport}>
            <i className="fa-solid fa-ellipsis-vertical"></i>
          </div>
          {isImportOpen && (
            <div className="import-ul">
              <ul>
                <ul>
                  <li><Link to='/customer-import'><i className="fa-solid fa-file-arrow-down"></i> Import Customers</Link></li>
                </ul>
                <ul>
                  <li><Link to='/export-customer'><i className="fa-solid fa-file-arrow-up"></i> Export Customers</Link></li>
                </ul>
                <ul>
                  <li onClick={handleRefresh}><i className="fa-solid fa-rotate"></i> Refresh List</li>
                </ul>
                <ul>
                  <li>
                    <a
                      href="/sample_customer.xlsx"
                      download
                      style={{ textDecoration: "none" }}
                    >
                      <i className="fa-solid fa-file-arrow-down"></i> Download Sample
                    </a>

                  </li>
                </ul>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="customer-search-summary">
        <div className="customer-search-summary__search">
          <Search className="customer-search-summary__icon" size={18} strokeWidth={2.25} aria-hidden />
          <input
            type="search"
            className="customer-search-summary__input"
            placeholder="Search by name, company, ID, phone, email, GST, remarks…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search customers"
          />
          {searchQuery.trim() !== "" && (
            <button
              type="button"
              className="customer-search-summary__clear customer-search-summary__clear--icon"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <X size={18} strokeWidth={2.25} aria-hidden />
            </button>
          )}
        </div>
        <div className="customer-search-summary__stats" role="status">
          <span className="customer-search-summary__pill" title="After date range filter">
            <strong>{totalInView}</strong> customer{totalInView === 1 ? "" : "s"}
          </span>
          {totalInView !== totalLoaded && (
            <span className="customer-search-summary__pill customer-search-summary__pill--muted">
              <strong>{totalLoaded}</strong> loaded overall
            </span>
          )}
          {searchActive && (
            <span className="customer-search-summary__pill customer-search-summary__pill--accent">
              <strong>{totalMatching}</strong> match{totalMatching === 1 ? "" : "es"} search
            </span>
          )}
          {totalMatching > 0 && (
            <span className="customer-search-summary__range">
              Rows {pageLabelFrom}–{pageLabelTo} of {totalMatching}
            </span>
          )}
        </div>
      </div>

      {filteredCustomers.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mt-4 mb-2 px-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allPageSelected}
              onChange={toggleSelectAllPage}
            />
            <span>Select all on this page</span>
          </label>
          {selectedIds.length > 0 && (
            <>
              <span className="text-sm text-gray-600">
                {selectedIds.length} selected
              </span>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5"
              >
                <Trash2 size={16} strokeWidth={2} />
                Delete selected
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </>
          )}
        </div>
      )}

      <div className="mt-5">
        {
        currentCustomers.length > 0 ? (
          currentCustomers.map((customer, index) => (
            <div
              key={customer.id || index}
              className="p-3 mb-4 border border-blue-100 rounded-lg shadow-sm hover:shadow-md hover:border-blue-300 transition-all bg-white flex gap-3 items-start"
            >
              <label
                className="mt-1 flex-shrink-0 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(customer.id)}
                  onChange={() => toggleSelected(customer.id)}
                />
              </label>
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => navigate(`/customer-detail/${customer.id}`)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    navigate(`/customer-detail/${customer.id}`);
                }}
              >
              {customer.customerType && (
                <div className="text-xs tracking-wide text-blue-600 bg-blue-50 rounded-full px-2 py-1  font-medium mb-1 inline-block">
                  {customer.customerType}
                </div>
              )}

              {customer.customerName && (
                <h2 className="text-base font-semibold text-gray-800 mb-2">
                  {customer.customerName} - {customer.cust_ID}
                </h2>
              )}

              {customer.phone1 && (
                <div className="flex items-center text-sm text-gray-700 mb-1">
                  <Phone className="w-4 h-4 text-blue-500 mr-2" strokeWidth={2} />
                  <span>{customer.phone1}</span>
                </div>
              )}

              {customer.email && (
                <div className="flex items-center text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-blue-500 mr-2" strokeWidth={2} />
                  <span>{customer.email}</span>
                </div>
              )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 text-sm my-12">No customers found.</p>
        )
      }

      </div>

      <div className="customer-bottom-bar" role="toolbar" aria-label="Customer actions">
        <button
          type="button"
          onClick={() => navigate('/add-customer')}
          className="cssbuttons-io-button customer-bottom-bar__add"
        >
          <span>Add Customer</span>
        </button>

        {filteredCustomers.length > entriesPerPage && (
          <div className="pagination customer-bottom-bar__pagination">
            <button
              type="button"
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
              aria-label="First page"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <button type="button" className="pagination-btn active" aria-current="page">
              {currentPage}
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
              aria-label="Last page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}
      </div>

      <FilterDrawer />
    </>
  );
};

export default Customer;
